import { useRouter } from 'next/router';
import { useEffect } from 'react';

import BackendService from '../services/backendService';
import {
  Template,
  TemplateEditOrCreateDTO,
} from '../services/TemplateService/Template.type';
import {
  buildScopeAndSequenceMetadata,
  getCurriculumTrackingProps,
  parseScopeAndSequenceParams,
  TemplateMetadata,
} from '../utils/scopeAndSequenceUtils';
import useDuplicateTemplate from './useDuplicateTemplate';
import { useMetrics } from './useMetrics';

// Module-level so React Strict Mode's dev remount (which resets refs) can't
// start a second duplicate of the same template. Cleared by any full page
// load, which is how every successful auto-copy ends. A failed attempt is
// marked 'failed' (not cleared) so revisiting the lesson via client-side
// navigation re-offers the manual copy modal instead of silently doing
// nothing — and instead of blindly retrying a duplicate that just failed.
const autoCopyStates = new Map<string, 'started' | 'failed'>();

/**
 * Auto-copies a shared lesson opened through a scope & sequence link
 * (utm_source=scope-and-sequence) instead of showing the copy-confirmation
 * modal. The S&S attribution is stamped into the copy's metadata before
 * redirecting so it survives further copying/sharing and is available to
 * downstream analytics events (Live.Start, share events, ...).
 */
function useScopeAndSequenceAutoCopy({
  templateId,
  tenantId,
  hasEditAccess,
  publicTemplate,
  isUserLoaded,
  onAutoCopyingChange,
  onFallbackToModal,
}: {
  templateId: string;
  tenantId: string;
  /**
   * Tri-state: `true` when the user can edit the lesson directly (owner, or a
   * flagged org member on a workspace lesson — org_members_can_edit_any_lesson),
   * `false` when definitely not, `undefined` while that's still being resolved
   * (template fetch in flight / Unleash flags not ready). Auto-copy is
   * irreversible, so it only runs on an explicit `false` — a user who can edit
   * the lesson should land in it as if they own it, never receive a copy.
   */
  hasEditAccess: boolean | undefined;
  publicTemplate: Template | undefined;
  isUserLoaded: boolean;
  onAutoCopyingChange: (isAutoCopying: boolean) => void;
  onFallbackToModal: (stampMetadata: TemplateMetadata) => void;
}) {
  const router = useRouter();
  const metrics = useMetrics();
  const { duplicateTemplate } = useDuplicateTemplate();

  const params = router.isReady ? parseScopeAndSequenceParams(router.query) : null;
  // Tri-state: `undefined` while the router hasn't parsed the query yet. The
  // copy-confirmation modal must wait for an explicit `false` — treating
  // "unknown" as "not an S&S link" would let the modal open (and accept a
  // manual duplicate) before auto-copy gets a chance to run.
  const isScopeAndSequenceAutoCopyLink: boolean | undefined = router.isReady
    ? !!params
    : undefined;

  // `params` is a fresh object every render — key the effect on its values so
  // it doesn't re-run each render (the 'failed' branch below dispatches, and a
  // per-render dispatch would loop).
  const paramsKey = params
    ? [params.curriculum, params.variant ?? '', params.lessonType ?? ''].join('|')
    : null;

  useEffect(() => {
    if (!params) return;
    // Only copy once edit access is ruled out with certainty — `undefined`
    // means the template fetch or the feature flags haven't resolved yet, and
    // duplicating can't be undone if edit access arrives a moment later.
    if (hasEditAccess !== false) return;
    if (!publicTemplate || !isUserLoaded) return;
    // The access handler still returns the template while raising
    // access_denied for sharing-blocked lessons — never duplicate those; let
    // the access-error modal handle it.
    if (publicTemplate.isBlockedFromSharing) return;
    if (autoCopyStates.get(templateId) === 'started') return;

    const stampMetadata = buildScopeAndSequenceMetadata(params, templateId);
    const trackingProps = getCurriculumTrackingProps(stampMetadata);

    // A previous attempt already failed this page-load (e.g. the user
    // navigated away from the fallback modal and came back) — re-offer the
    // manual modal instead of retrying the duplicate or doing nothing.
    if (autoCopyStates.get(templateId) === 'failed') {
      onFallbackToModal(stampMetadata);
      return;
    }
    autoCopyStates.set(templateId, 'started');

    metrics.logEvent('ScopeAndSequence.Lesson.AutoCopy', {
      templateId,
      ...trackingProps,
    });
    onAutoCopyingChange(true);

    duplicateTemplate({
      templateId,
      isPrivate: true,
      onSuccessFunction: async (copy: Template) => {
        try {
          // Stamp attribution before redirecting — the redirect is a full page
          // load, so anything not persisted by then is lost.
          await BackendService.instance.template.templateAutoSave(
            {
              id: copy.id,
              modifiedAt: new Date().getTime(),
              metadata: {
                ...(copy.metadata ?? {}),
                ...stampMetadata,
              },
            } as TemplateEditOrCreateDTO,
            tenantId,
          );
          metrics.logEvent('ScopeAndSequence.Lesson.AutoCopy.Success', {
            templateId,
            copiedTemplateId: copy.id,
            ...trackingProps,
          });
        } catch {
          // Never strand the user on a dead page over a failed stamp.
          metrics.logEvent('ScopeAndSequence.Lesson.AutoCopy.StampFailed', {
            templateId,
            copiedTemplateId: copy.id,
            ...trackingProps,
          });
        }
        // Use window.location for a full page load instead of client-side
        // navigation. This ensures we correctly generate a new game from the
        // template (same as LessonCopyConfirmationModal).
        window.location.href = `/${tenantId}/lessons/${copy.id}/edit`;
      },
      onErrorFunction: () => {
        // Mark failed (not cleared) so a revisit re-offers the manual modal
        // rather than silently doing nothing or blindly retrying.
        autoCopyStates.set(templateId, 'failed');
        metrics.logEvent('ScopeAndSequence.Lesson.AutoCopy.Failed', {
          templateId,
          ...trackingProps,
        });
        onAutoCopyingChange(false);
        // Fall back to the manual copy-confirmation modal, handing it the
        // S&S metadata so a manual copy still gets stamped with attribution.
        onFallbackToModal(stampMetadata);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paramsKey,
    hasEditAccess,
    publicTemplate,
    isUserLoaded,
    templateId,
    tenantId,
    metrics,
    duplicateTemplate,
    onAutoCopyingChange,
    onFallbackToModal,
  ]);

  return { isScopeAndSequenceAutoCopyLink };
}

export default useScopeAndSequenceAutoCopy;
